import { assign, fromPromise, setup } from 'xstate'
import sentryCapture from '~/utility/sentryCapture'
import { SearchFilters } from '../search/schemas'
import { AllocationAlertRepositoryFetch } from './AllocationAlertRepositoryFetch'
import { AllocationAlertPayloadCampsiteDetails } from './types'

type Context = {
  langCode: string
  errorMessages?: string[]
}

type Events =
  | {
      type: 'SUBMIT_CAMPSITE_DETAILS'
      payload: AllocationAlertPayloadCampsiteDetails
    }
  | {
      type: 'SUBMIT_SEARCH_RESULTS'
      email: string
      searchFilters: SearchFilters
    }

type CampsiteDetailsInput = {
  payload: AllocationAlertPayloadCampsiteDetails
}

type SearchResultsInput = {
  email: string
  searchFilters: SearchFilters
  langCode: string
}

export function getAllocationAlertStateMachine(langCode: string) {
  return setup({
    actions: {
      logError: ({ event }) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type XState error events properly
        if (!event || !event.data || !event.data.response) {
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type XState error events properly
        const response = event.data.response
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type XState error events properly
        if (response && (response.status < 500 || response.statusCode < 500)) {
          return
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: Type XState error events properly
        event.data.name = `allocationAlertStateMachine ${event.type} error`
        sentryCapture(event.data)
      },
      setErrorMessages: assign({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return -- TODO: Type XState error events properly
        errorMessages: ({ event }) => event.error.data,
      }),
    },
    actors: {
      alertCampsiteDetailsSubscriptionService: fromPromise(
        async ({ input }: { input: CampsiteDetailsInput }) => {
          const repo = new AllocationAlertRepositoryFetch()
          return await repo.subscribeToCampsiteDetails(input.payload)
        },
      ),
      alertSearchResultsSubscriptionService: fromPromise(
        async ({ input }: { input: SearchResultsInput }) => {
          const repo = new AllocationAlertRepositoryFetch()
          return await repo.subscribeToSearchResults(
            input.email,
            input.searchFilters,
            input.langCode,
          )
        },
      ),
    },
  }).createMachine<Context, Events>({
    id: 'AllocationAlertMachine',
    initial: 'idle',
    context: { langCode },
    states: {
      idle: {
        on: {
          SUBMIT_CAMPSITE_DETAILS: { target: 'submitting.campsiteDetails' },
          SUBMIT_SEARCH_RESULTS: { target: 'submitting.searchResults' },
        },
      },
      submitting: {
        initial: 'idle',
        states: {
          idle: {},
          campsiteDetails: {
            invoke: {
              src: 'alertCampsiteDetailsSubscriptionService',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type XState event payloads properly
              input: ({ event: { payload } }) => ({ payload }),
              onDone: {
                target: '#AllocationAlertMachine.submitted',
              },
              onError: {
                target: '#AllocationAlertMachine.error',
                actions: 'setErrorMessages',
              },
            },
          },
          searchResults: {
            invoke: {
              src: 'alertSearchResultsSubscriptionService',
              input: ({
                event: { email, searchFilters },
                context: { langCode },
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: Type XState event payloads properly
              }) => ({ email, searchFilters, langCode }),
              onDone: {
                target: '#AllocationAlertMachine.submitted',
              },
              onError: {
                target: '#AllocationAlertMachine.error',
                actions: 'setErrorMessages',
              },
            },
          },
        },
      },
      submitted: {
        type: 'final',
      },
      error: {
        onEntry: 'logError',
        on: {
          SUBMIT_CAMPSITE_DETAILS: { target: 'submitting.campsiteDetails' },
          SUBMIT_SEARCH_RESULTS: { target: 'submitting.searchResults' },
        },
      },
    },
  })
}
